import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Settings, TrendingDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockAssets, assetDepreciationData } from '@/lib/mock-data';
import { formatCurrency as formatCurrencyUtil, calculateYAxisTicks, normalizeToSVG } from '@/lib/chart-utils';
import type { Asset, Transaction } from '@/types/asset';

interface DepreciationDataPoint {
  month: string;
  value: number;
}

interface DepreciationYTDCardProps {
  readonly currentValue: number; // Mock data for fallback
  readonly monthlyData: DepreciationDataPoint[]; // Mock data for graph visualization (always used)
  readonly assets?: Asset[]; // Real-time API data
  readonly transactions?: Transaction[]; // Real-time API data
  readonly className?: string;
}

export function DepreciationYTDCard({
  currentValue: defaultCurrentValue,
  monthlyData: defaultMonthlyData,
  assets,
  transactions,
  className,
}: DepreciationYTDCardProps): React.ReactElement {
  const [hoveredPoint, setHoveredPoint] = useState<DepreciationDataPoint | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string>('all');
  const navigate = useNavigate();

  // Get sorted assets by name from API data or fallback to mock data
  const sortedAssets = useMemo(() => {
    const assetList = assets && assets.length > 0 ? assets : mockAssets;
    return [...assetList]
      .filter((asset: Asset) => asset.status === 'inService') // Only show in-service assets
      .sort((a: Asset, b: Asset) => a.name.localeCompare(b.name));
  }, [assets]);

  const getValidNumber = (value: unknown): number => {
    return typeof value === 'number' && !isNaN(value) ? value : 0;
  };

  const calculateTransactionTotals = (txns: Transaction[] | undefined, assetIdFilter?: string) => {
    let additions = 0;
    let revaluations = 0;
    if (txns && txns.length > 0) {
      txns.forEach(transaction => {
        if (assetIdFilter && transaction.assetId !== assetIdFilter) return;
        const amount = getValidNumber(transaction.amount);
        const transactionType = transaction.type.toLowerCase();
        if (transactionType === 'addition' || transactionType === 'acquisition') {
          additions += amount;
        } else if (transactionType === 'revaluation') {
          revaluations += amount;
        }
      });
    }
    return { additions, revaluations };
  };

  const currentValue = useMemo(() => {
    try {
      if (!assets || assets.length === 0) {
        return defaultCurrentValue;
      }

      if (selectedAssetId === 'all') {
        const totalNBV = assets.reduce((sum: number, asset: Asset) => sum + getValidNumber(asset.netBookValue), 0);
        const { additions, revaluations } = calculateTransactionTotals(transactions);
        return Math.max(0, totalNBV - additions - revaluations);
      }

      const selectedAsset = assets.find(a => a.id === selectedAssetId);
      if (!selectedAsset) {
        return defaultCurrentValue;
      }

      const nbv = getValidNumber(selectedAsset.netBookValue);
      const { additions, revaluations } = calculateTransactionTotals(transactions, selectedAssetId);
      return Math.max(0, nbv - additions - revaluations);
    } catch {
      return defaultCurrentValue;
    }
  }, [assets, transactions, selectedAssetId, defaultCurrentValue]);

  /**
   * Calculate Starting Value (Acquisition Cost) from API data
   * Formula: Sum of Acquisition Costs for all assets or specific asset
   * 
   * - For 'all': Sum of all assets' acquisition costs
   * - For specific asset: Asset's acquisition cost
   * - Handles null/undefined values gracefully
   */
  const startingValue = useMemo(() => {
    try {
      // If no assets data available, use fallback from mock data
      if (!assets || assets.length === 0) {
        return defaultMonthlyData[0]?.value ?? 0;
      }

      if (selectedAssetId === 'all') {
        // Calculate sum of all assets' acquisition costs
        const totalAcquisitionCost = assets.reduce((sum: number, asset: Asset) => {
          const cost = typeof asset.acquisitionCost === 'number' && !isNaN(asset.acquisitionCost) 
            ? asset.acquisitionCost 
            : 0;
          return sum + cost;
        }, 0);
        
        return totalAcquisitionCost;
      } else {
        // Get specific asset's acquisition cost
        const selectedAsset = assets.find(a => a.id === selectedAssetId);
        if (selectedAsset) {
          const cost = typeof selectedAsset.acquisitionCost === 'number' && !isNaN(selectedAsset.acquisitionCost)
            ? selectedAsset.acquisitionCost
            : 0;
          
          return cost;
        }
        
        // Asset not found, use fallback
        return defaultMonthlyData[0]?.value ?? 0;
      }
    } catch {
      return defaultMonthlyData[0]?.value ?? 0;
    }
  }, [assets, selectedAssetId, defaultMonthlyData]);

  /**
   * Get mock data for graph visualization
   * Graph ALWAYS uses mock data, never API data
   */
  const monthlyData = useMemo(() => {
    if (selectedAssetId === 'all') {
      // Return default aggregated mock data
      return defaultMonthlyData;
    }

    // Get specific asset mock data or generate fallback
    const assetData = assetDepreciationData[selectedAssetId];
    if (assetData) {
      return assetData.monthlyData;
    }

    // Fallback: Generate realistic declining exponential curve for assets without specific data
    const assetList = assets && assets.length > 0 ? assets : mockAssets;
    const asset = assetList.find(a => a.id === selectedAssetId);
    if (asset) {
      const assetCurrentValue = asset.netBookValue;
      const totalDepreciation = asset.accumulatedDepreciation;
      
      // Calculate starting value (current NBV + accumulated depreciation)
      const mockStartingValue = assetCurrentValue + totalDepreciation;
      
      // Generate exponential decay curve (higher depreciation early, slowing down)
      // Using exponential decay formula: value = start * e^(-k*t)
      const decayRate = 0.15; // Decay constant for realistic depreciation
      
      return [
        { month: 'Apr', value: mockStartingValue * Math.exp(-decayRate * 0) },
        { month: 'May', value: mockStartingValue * Math.exp(-decayRate * 1) },
        { month: 'Jun', value: mockStartingValue * Math.exp(-decayRate * 2) },
        { month: 'Jul', value: mockStartingValue * Math.exp(-decayRate * 3) },
        { month: 'Aug', value: mockStartingValue * Math.exp(-decayRate * 4) },
        { month: 'Sep', value: mockStartingValue * Math.exp(-decayRate * 5) },
        { month: 'Oct', value: mockStartingValue * Math.exp(-decayRate * 6) },
        { month: 'Nov', value: assetCurrentValue }, // End at current NBV
      ];
    }

    // Ultimate fallback
    return defaultMonthlyData;
  }, [selectedAssetId, assets, defaultMonthlyData]);

  // Calculate percentage change from start of year (using API starting value)
  const percentageChange = startingValue > 0 ? ((startingValue - currentValue) / startingValue) * 100 : 0;

  // Handle card click to navigate to depreciation page
  const handleCardClick = () => {
    navigate('/transactions/depreciation');
  };

  // Calculate Y-axis ticks and scaling
  const values = monthlyData.map(d => d.value);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const yAxisTicks = useMemo(() => calculateYAxisTicks(dataMin, dataMax, 5), [dataMin, dataMax]);
  const minValue = yAxisTicks[0];
  const maxValue = yAxisTicks[yAxisTicks.length - 1];

  // Format currency
  const formatCurrency = (value: number) => {
    return `£${value.toLocaleString('en-GB')}`;
  };

  return (
    <Card 
      className={cn(
        'relative overflow-hidden shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 bg-white dark:bg-black',
        className
      )}
      onClick={handleCardClick}
    >

      <CardHeader className="relative flex flex-row items-start justify-between space-y-0 pb-3">
        <div className="space-y-1.5">
          <CardTitle className="text-base font-semibold tracking-tight">
            Depreciation (YTD)
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Year-to-date depreciation trend
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Info className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" align="end" className="w-80 p-4" sideOffset={8}>
              <div className="space-y-2">
                <div className="text-sm font-semibold">What does this mean?</div>
                <div className="text-sm text-muted-foreground leading-relaxed">
                  This chart shows the cumulative depreciation of your assets from the start
                  of the fiscal year (April) to the current month. The descending curve
                  represents the reduction in asset book values over time due to wear,
                  obsolescence, and usage.
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Settings className="h-4 w-4" />
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" align="end" className="w-64 p-3" sideOffset={8}>
              <div className="text-sm text-muted-foreground">
                Click to view detailed depreciation transactions
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>

      <CardContent className="relative space-y-4">
        {/* Asset Filter */}
        <div className="flex items-center gap-2">
          <label htmlFor="asset-filter" className="text-sm font-medium text-muted-foreground whitespace-nowrap">
            Asset:
          </label>
          <Select value={selectedAssetId} onValueChange={setSelectedAssetId}>
            <SelectTrigger 
              id="asset-filter"
              className="h-9 w-full"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <SelectValue placeholder="Select asset" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <SelectItem value="all">All</SelectItem>
              {sortedAssets.map((asset) => (
                <SelectItem key={asset.id} value={asset.id}>
                  {asset.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Depreciation Value Display */}
        <div className="rounded-lg border bg-muted/30 px-4 py-3">
          <div className="text-xs font-medium text-muted-foreground mb-1">
            Current Depreciation Value
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {formatCurrency(currentValue)}
          </div>
          <div className="flex items-center gap-1.5 text-xs mt-1">
            <TrendingDown className="h-3 w-3 text-blue-500" />
            <span className="font-medium text-blue-500">
              {percentageChange.toFixed(1)}%
            </span>
            <span className="text-muted-foreground">since April</span>
          </div>
        </div>

        {/* Line Graph */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Fiscal Year Progress</span>
            <span className="font-medium">
              {monthlyData[0]?.month} → {monthlyData[monthlyData.length - 1]?.month}
            </span>
          </div>
          <div className="relative h-40 w-full">
            <svg
              viewBox="0 0 600 160"
              className="h-full w-full"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Y-Axis */}
              <line
                x1="70"
                y1="20"
                x2="70"
                y2="130"
                stroke="hsl(var(--foreground))"
                strokeWidth="2"
                opacity="0.3"
              />
              
              {/* Y-Axis Ticks and Labels */}
              {yAxisTicks.map((tick) => {
                const y = normalizeToSVG(tick, minValue, maxValue, 160, 20);
                return (
                  <g key={`tick-${tick}`}>
                    {/* Tick mark */}
                    <line
                      x1="63"
                      y1={y}
                      x2="70"
                      y2={y}
                      stroke="hsl(var(--foreground))"
                      strokeWidth="2"
                      opacity="0.3"
                    />
                    {/* Grid line */}
                    <line
                      x1="70"
                      y1={y}
                      x2="580"
                      y2={y}
                      stroke="hsl(var(--border))"
                      strokeWidth="0.5"
                      strokeDasharray="2,2"
                      opacity="0.2"
                    />
                    {/* Label */}
                    <text
                      x="60"
                      y={y + 4}
                      textAnchor="end"
                      fontSize="9"
                      fill="hsl(var(--muted-foreground))"
                      fontWeight="500"
                      className="select-none"
                    >
                      {formatCurrencyUtil(tick)}
                    </text>
                  </g>
                );
              })}

              {/* Gradient fill under line */}
              <defs>
                <linearGradient id="depreciationGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="hsl(217 91% 60%)" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="hsl(217 91% 60%)" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Area under the line */}
              <polygon
                points={`70,130 ${monthlyData
                  .map((dataPoint, index) => {
                    const x = 80 + index * 65;
                    const y = normalizeToSVG(dataPoint.value, minValue, maxValue, 160, 20);
                    return `${x},${y}`;
                  })
                  .join(' ')} ${80 + (monthlyData.length - 1) * 65},130`}
                fill="url(#depreciationGradient)"
              />

              {/* Line path */}
              <polyline
                points={monthlyData
                  .map((dataPoint, index) => {
                    const x = 80 + index * 65;
                    const y = normalizeToSVG(dataPoint.value, minValue, maxValue, 160, 20);
                    return `${x},${y}`;
                  })
                  .join(' ')}
                fill="none"
                stroke="hsl(217 91% 60%)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data points with hover */}
              {monthlyData.map((dataPoint, index) => {
                const x = 80 + index * 65;
                const y = normalizeToSVG(dataPoint.value, minValue, maxValue, 160, 20);
                const isHovered = hoveredPoint?.month === dataPoint.month;

                return (
                  <g key={`datapoint-${dataPoint.month}`}>
                    <circle
                      cx={x}
                      cy={y}
                      r={isHovered ? 5 : 3.5}
                      fill="hsl(217 91% 60%)"
                      stroke="hsl(var(--background))"
                      strokeWidth="2"
                      className="cursor-pointer transition-all"
                      onMouseEnter={() => setHoveredPoint(dataPoint)}
                      onMouseLeave={() => setHoveredPoint(null)}
                    />
                    
                    {/* Month label */}
                    <text
                      x={x}
                      y="145"
                      textAnchor="middle"
                      fontSize="9"
                      fill="hsl(var(--muted-foreground))"
                      fontWeight="500"
                      className="select-none"
                    >
                      {dataPoint.month}
                    </text>

                    {/* Hover tooltip */}
                    {isHovered && (
                      <g>
                        <rect
                          x={x - 35}
                          y={y - 38}
                          width="70"
                          height="28"
                          rx="4"
                          fill="hsl(var(--popover))"
                          stroke="hsl(var(--border))"
                          strokeWidth="1"
                          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                        />
                        <text
                          x={x}
                          y={y - 25}
                          textAnchor="middle"
                          fontSize="9"
                          fontWeight="600"
                          fill="hsl(var(--muted-foreground))"
                        >
                          {dataPoint.month}
                        </text>
                        <text
                          x={x}
                          y={y - 14}
                          textAnchor="middle"
                          fontSize="10"
                          fontWeight="700"
                          fill="hsl(217 91% 60%)"
                        >
                          {formatCurrency(dataPoint.value)}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border bg-muted/50 px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">
              Starting Value
            </div>
            <div className="text-sm font-semibold text-foreground">
              {formatCurrency(startingValue)}
            </div>
          </div>
          <div className="rounded-lg border bg-muted/50 px-3 py-2">
            <div className="text-xs font-medium text-muted-foreground">
              Current Value
            </div>
            <div className="text-sm font-semibold text-blue-600">
              {formatCurrency(currentValue)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
